import * as React from 'react'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'

import Grid from '@mui/material/Grid'

import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import LoadingButton from '@mui/lab/LoadingButton'

import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import StepButton from '@mui/material/StepButton'

import Typography from '@mui/material/Typography'

import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined'
import EditableSelect from '../EditableSelect'
import MultipleSelect from '../MultiSelect'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'

import { useZustand } from '../../hooks/useZustand'
import {
    useContextsStore,
    selectOwnedContexts,
} from '../../stores/useContextsStore'
import usePageInfo from '../../hooks/usePageInfo'

import { UserContext } from '../../UserContext'

import { useTheme } from '@mui/material/styles'

import { SubImportContext } from './SubImportContext'
import { DialogsContext } from '../../DialogsContext'

import FormControl from '@mui/material/FormControl'

import axios from 'axios'

import { useDropzone } from 'react-dropzone'
import {
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    ListItemSecondaryAction,
} from '@mui/material'
import { InsertDriveFile, Delete } from '@mui/icons-material'
import { slugifyString } from '../../utils'
import ImportingNotice from './ImportingNotice'
import { importErrorMessage } from './importError'

const addPrivateStatementsSelector = state => state.addPrivateStatementsToStore

const steps = ['File', 'Destination', 'Process']

const AUTO_SELECTED_TAG_FIELDS = [
    'relation',
    'confidence',
    'confidence_score',
    'weight',
]

export default function NetworkXImportDialog({ presetContext, open, onClose }) {
    const theme = useTheme() // hook to get the current theme

    const { label, location } = usePageInfo()

    const { activeSubImportDialog } = React.useContext(SubImportContext)

    const { presetFiles, closeImportDialog } = React.useContext(DialogsContext)

    const preSelectedContext = presetContext
        ? presetContext
        : generatePresetContext()

    const [selectedContext, addToSelectedContext] = React.useState(
        preSelectedContext
    )

    const [errorMessage, setErrorMessage] = React.useState(null)

    const [importError, setImportError] = React.useState(null)

    const [selectedFeeds, setSelectedFeeds] = React.useState([])

    const [completedSteps, setCompletedSteps] = React.useState([])

    const [visitedContext, setVisitedContext] = React.useState(false)

    const [doNotSaveGraph, setDoNotSaveGraph] = React.useState(false)

    const [fromInsideContext, setFromInsideContext] = React.useState(false)

    const { username } = React.useContext(UserContext)
    const addPrivateStatementsToStore = useZustand(addPrivateStatementsSelector)

    React.useEffect(() => {
        if (location == 'context' && label) {
            setFromInsideContext(true)
        }
    }, [label])

    const contexts = useContextsStore(selectOwnedContexts)
    const contextNames = contexts.map(c => c.name)

    const [activeStep, setActiveStep] = React.useState(0)
    const [skipped, setSkipped] = React.useState(new Set())

    const isStepSkipped = step => {
        return skipped.has(step)
    }

    const handleNext = () => {
        let newSkipped = skipped
        if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values())
            newSkipped.delete(activeStep)
        }

        setActiveStep(prevActiveStep => prevActiveStep + 1)
        setSkipped(newSkipped)
    }

    const [files, setFiles] = React.useState(presetFiles)

    const [fileName, setFileName] = React.useState('')

    const [edgeFields, setEdgeFields] = React.useState([])
    const [selectedTagFields, setSelectedTagFields] = React.useState([])
    const [addFieldNameToTags, setAddFieldNameToTags] = React.useState(true)
    const [addSourceFileToText, setAddSourceFileToText] = React.useState(false)
    const [hasSourceFileField, setHasSourceFileField] = React.useState(false)

    const fileInputRef = React.useRef()

    React.useEffect(() => {
        if (!presetContext)
            addToSelectedContext(
                generatePresetContext(
                    'networkx',
                    slugifyString(fileName).slice(0, 12)
                )
            )
    }, [fileName])

    const onDrop = React.useCallback(acceptedFiles => {
        const droppedFiles = acceptedFiles
        handleAddedFiles(droppedFiles)
    }, [])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        noClick: true,
        noKeyboard: true,
    })

    const onFilesAdded = event => {
        const addedFiles = Array.from(event.target.files)
        handleAddedFiles(addedFiles)
    }

    React.useEffect(() => {
        const existPresetFiles =
            presetFiles && presetFiles.length > 0 ? true : false
        if (existPresetFiles) handleAddedFiles(presetFiles)
    }, [])

    const handleAddedFiles = addedFiles => {
        const uploadedFiles = addedFiles.filter(
            file => determineFileType(file) === '.json'
        )

        setFiles([...uploadedFiles])

        if (uploadedFiles.length > 0) discoverEdgeFields(uploadedFiles[0])
    }

    // Read the file in the browser to list the edge fields the user can
    // turn into statement tags. Parse errors are ignored here — the server
    // reports them properly on submit.
    const discoverEdgeFields = file => {
        const reader = new FileReader()
        reader.onload = event => {
            try {
                const parsed = JSON.parse(event.target.result)
                const links = Array.isArray(parsed.links)
                    ? parsed.links
                    : Array.isArray(parsed.edges)
                    ? parsed.edges
                    : []
                const fields = new Set()
                links.forEach(link =>
                    Object.keys(link).forEach(key => {
                        if (key !== 'source' && key !== 'target')
                            fields.add(key)
                    })
                )
                setEdgeFields(Array.from(fields))
                setSelectedTagFields(
                    AUTO_SELECTED_TAG_FIELDS.filter(field => fields.has(field))
                )
                setHasSourceFileField(links.some(link => link.source_file))
            } catch {
                setEdgeFields([])
                setSelectedTagFields([])
                setHasSourceFileField(false)
            }
        }
        reader.readAsText(file)
    }

    const handleDelete = fileToDelete => () => {
        setFiles(prevFiles => prevFiles.filter(file => file !== fileToDelete))
    }

    const openFileDialog = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    const fileList = files.reverse().map((file, index) => {
        return (
            <ListItem key={index}>
                <ListItemIcon>
                    <InsertDriveFile />
                </ListItemIcon>
                <ListItemText
                    primary={file.name}
                    secondary={`${file.size} bytes`}
                />
                <ListItemSecondaryAction>
                    <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={handleDelete(file)}
                    >
                        <Delete />
                    </IconButton>
                </ListItemSecondaryAction>
            </ListItem>
        )
    })

    const submitDataForm = () => {
        const formData = new FormData()

        // Append the files to the FormData object
        files.forEach(file => {
            formData.append('uploadedFile', file) // 'files' is the key for the server
        })

        formData.append('source', 'file')

        formData.append('context', selectedContext)

        if (doNotSaveGraph) {
            formData.append('doNotSaveGraph', 'true')
        }

        formData.append('tagFields', JSON.stringify(selectedTagFields))
        formData.append('addFieldNameToTags', String(addFieldNameToTags))
        if (addSourceFileToText) {
            formData.append('addSourceFileToText', 'true')
        }

        axios
            .post('/import/networkx', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
            .then(response => {
                if (response.data.redirectUrl) {
                    window.location.href = response.data.redirectUrl
                } else {
                    if (response.data.error) {
                        setErrorMessage(
                            typeof response.data.error == 'object'
                                ? response.data.error.error ||
                                      JSON.stringify(response.data.error)
                                : response.data.error
                        )
                        setImportError(response.data.error)
                        setActiveStep(0)
                    }
                    if (response.data.entryuid) {
                        const postedby = username
                        const res = response.data

                        // Handle private mode statements
                        if (doNotSaveGraph && res.statements) {
                            // Private mode: add statements to Zustand store without saving to DB
                            const entryuids = JSON.parse(res.entryuid)
                            const statementHashtags =
                                res.statementHashtags || {}
                            addPrivateStatementsToStore(
                                res.statements,
                                selectedContext,
                                entryuids,
                                statementHashtags
                            )
                        }

                        if (
                            window.myEventEmitter &&
                            myEventEmitter._events['chatMessage']
                        ) {
                            myEventEmitter.emit('chatMessage', {
                                source: null,
                                postedby: postedby,
                                entryuid: JSON.parse(res.entryuid),
                                entrytext: JSON.parse(res.entrytext),
                                entryname: JSON.parse(res.entryname),
                                tags: JSON.parse(res.tags),
                                timestamp: JSON.parse(res.timestamp),
                                graph: res.graph,
                                stimulateVariability: false,
                                message: res.message,
                                statementHashtags: res.statementHashtags || {},
                                doNotSaveGraph: doNotSaveGraph,
                            })
                            closeImportDialog()
                        }
                    }
                }
            })
            .catch(error => {
                console.error('Received the following error', error)
                setErrorMessage(importErrorMessage(error))
                setImportError(error)
            })
    }

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1)
    }

    React.useEffect(() => {
        if (activeSubImportDialog.content) {
            setActiveStep(1)
            setSelectedFeeds(activeSubImportDialog.content)
        }
    }, [activeSubImportDialog])

    React.useEffect(() => {
        setImportError(null)
    }, [selectedFeeds])

    React.useEffect(() => {
        switch (activeStep) {
            case 1:
                if (files.length > 0) {
                    if (files[0].name)
                        setFileName(
                            files[0].name.split('.')[0].replace(/ /g, '_')
                        )

                    setCompletedSteps([...completedSteps, 0, 1])
                    setErrorMessage(null)
                    setVisitedContext(true)

                    if (!presetContext) {
                        addToSelectedContext(
                            generatePresetContext('networkx', fileName)
                        )
                    }

                    break
                }
            case 2:
                if (selectedContext) {
                    setCompletedSteps([...completedSteps, 3])
                    setErrorMessage(null)
                    submitDataForm()
                    break
                }
                setActiveStep(2)
                break
        }
    }, [activeStep])

    const handleStep = step => {
        setActiveStep(step)
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth={true}
                maxWidth="lg"
            >
                <DialogTitle
                    sx={{
                        padding: 4,
                        color: theme => theme.palette.text.primary,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {'Analyze a NetworkX / Graphify (.json) File'}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Stepper
                    activeStep={activeStep}
                    sx={{ paddingLeft: 4, paddingRight: 4, paddingTop: 2 }}
                >
                    {steps.map((label, index) => {
                        const stepProps = {}
                        const labelProps = {}
                        if (isStepSkipped(index)) {
                            stepProps.completed = false
                        }
                        return (
                            <Step key={label} {...stepProps}>
                                <StepButton
                                    onClick={() => handleStep(index)}
                                    completed={(
                                        completedSteps.indexOf(index) >= 0
                                    ).toString()}
                                >
                                    <StepLabel
                                        {...labelProps}
                                        sx={{
                                            '& .MuiStepLabel-label': {
                                                fontSize: {
                                                    xs: '0.75rem',
                                                    sm: 'initial',
                                                },
                                            },
                                        }}
                                    >
                                        {label}
                                    </StepLabel>
                                </StepButton>
                            </Step>
                        )
                    })}
                </Stepper>

                {errorMessage && (
                    <Typography
                        sx={{
                            paddingRight: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                            paddingBottom: 0,
                        }}
                        color="error"
                        component="div"
                        gutterBottom
                    >
                        {errorMessage}
                    </Typography>
                )}

                {activeStep === 0 && (
                    <>
                        <DialogContent
                            sx={{
                                paddingRight: 4,
                                paddingLeft: 4,
                                paddingTop: 4,
                            }}
                        >
                            <Box sx={{ p: 2 }}>
                                <Paper
                                    {...getRootProps()}
                                    onClick={openFileDialog}
                                    sx={{
                                        p: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        height: '200px',
                                        borderStyle: 'dashed',
                                        borderColor: isDragActive
                                            ? 'primary.main'
                                            : 'grey.400',
                                        backgroundColor: isDragActive
                                            ? 'background.default'
                                            : 'background.paper',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Typography variant="body1">
                                        Drag and drop the NetworkX node-link
                                        .json file you'd like to analyze here
                                        (e.g. the graph.json produced by
                                        Graphify), or click to select and
                                        upload manually.
                                    </Typography>

                                    <input
                                        {...getInputProps()}
                                        ref={fileInputRef}
                                        accept=".json"
                                        onChange={onFilesAdded}
                                        style={{ display: 'none' }}
                                        multiple={false}
                                    />
                                </Paper>
                                <Box sx={{ m: 2 }}>
                                    <Button
                                        variant="contained"
                                        component="label"
                                        onClick={openFileDialog}
                                    >
                                        Select & Upload New File
                                    </Button>
                                </Box>

                                <List>{fileList}</List>
                            </Box>
                        </DialogContent>
                    </>
                )}

                {activeStep === 1 && (
                    <>
                        <DialogContent
                            sx={{
                                paddingRight: 4,
                                paddingLeft: 4,
                                paddingTop: 4,
                            }}
                        >
                            <Grid container spacing={8}>
                                <Grid item xs={12} sm={5}>
                                    <DialogContentText>
                                        Please, choose the context to import
                                        your content to:
                                    </DialogContentText>
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 4 }}
                                    >
                                        <EditableSelect
                                            selectItems={contextNames}
                                            label={'Contexts'}
                                            initialValue={selectedContext}
                                            onChange={name =>
                                                addToSelectedContext(name)
                                            }
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <DialogContentText>
                                        The graph's edges will be imported as
                                        statements connecting the nodes.
                                        Choose which edge fields to add as
                                        statement tags you can filter by —
                                        numeric fields are grouped into
                                        ranges.
                                    </DialogContentText>
                                    {edgeFields.length > 0 && (
                                        <MultipleSelect
                                            selectItems={edgeFields}
                                            label={'Edge fields to add as tags'}
                                            initialValue={selectedTagFields}
                                            onChange={fields =>
                                                setSelectedTagFields(fields)
                                            }
                                        />
                                    )}
                                    <Box>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={addFieldNameToTags}
                                                    onChange={event =>
                                                        setAddFieldNameToTags(
                                                            event.target
                                                                .checked
                                                        )
                                                    }
                                                />
                                            }
                                            label="Prefix tags with field name"
                                        />
                                    </Box>
                                    {hasSourceFileField && (
                                        <Box>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={
                                                            addSourceFileToText
                                                        }
                                                        onChange={event =>
                                                            setAddSourceFileToText(
                                                                event.target
                                                                    .checked
                                                            )
                                                        }
                                                    />
                                                }
                                                label="Add source file to statement text"
                                            />
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </>
                )}
                {activeStep === 2 && (
                    <ImportingNotice
                        linkHref={`/${username}/${selectedContext}/edit`}
                    />
                )}
                <DialogActions
                    sx={{
                        paddingRight: 4,
                        paddingLeft: 4,
                        paddingBottom: 4,
                        paddingTop: 2,
                    }}
                >
                    <Button
                        color="inherit"
                        variant="outlined"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ mr: 1, px: 3, py: 1, fontSize: '0.95rem' }}
                    >
                        Back
                    </Button>
                    <LoadingButton
                        color="info"
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        disabled={!files.length > 0}
                        onClick={handleNext}
                        sx={{ px: 4, py: 1, fontSize: '0.95rem' }}
                    >
                        {activeStep === steps.length - 1 ? 'Visualize' : 'Next'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}

function generatePresetContext(type, fileName) {
    const dateAndTime = new Date()
        .toISOString()
        .slice(2, 16)
        .replace(/-/g, '')
        .replace(/:/g, '')

    const typeString = type ? `${slugifyString(type)}_` : 'networkx_'
    const nameString = fileName
        ? slugifyString(fileName).slice(0, 12)
        : dateAndTime

    return `${typeString}${nameString}`
}

const determineFileType = file => {
    if (
        file.name.toLowerCase().endsWith('.json') &&
        (!file.type ||
            file.type === 'application/json' ||
            file.type === 'application/octet-stream' ||
            file.type === 'text/plain')
    ) {
        return '.json'
    } else {
        return null
    }
}
